'use client'

import Image from 'next/image'
import {FC, useRef} from 'react'

import {Section} from '@/components/common/content/Section'
import {Hero} from '@/components/common/hero/Hero'
import {MostRecentArticles} from '@/components/common/recent-articles/MostRecentArticles'
import {LegalTeam} from '@/components/common/team/LegalTeam'
import {Page} from '@/components/Page'
import {scrollToServiceRequestForm} from '@/components/service-request/helpers'
import {HowServiceRequestWorks} from '@/components/service-request/HowServiceRequestWorks'
import {ResponseToOppositionForm} from '@/components/service-request/response-to-opposition/ResponseToOppositionForm'
import {ServiceRequestFaqs} from '@/components/service-request/ServiceRequestFaqs'
import {ServiceRequestPricingTable} from '@/components/service-request/ServiceRequestPricingTable'
import {ServiceRequestSolutions} from '@/components/service-request/ServiceRequestSolutions'
import {ServiceRequestStatistics} from '@/components/service-request/ServiceRequestStatistics'
import {Testimonial} from '@/components/testimonial/Testimonial'
import {SelectedIpCases} from '@/components/trademark-disputes/home/ip-cases/SelectedIpCases'
import {useServiceCommonContent} from '@/content/service-request/common'
import {getResponseToOppositionContent} from '@/content/service-request/responseToOpposition'
import {useTranslation} from '@/i18n'
import {HERO_PADDING_DESKTOP, HERO_PADDING_MOBILE} from '@/theme/sizing'
import {theme} from '@/theme/theme'
import {Locale} from '@/utils/constants'
import {RecentArticleCard} from '@/utils/contentful'
import {useLanguage} from '@/utils/i18n/language'
import {QuestionsAndAnswersRelatedQuestion} from '@/utils/questions-and-answers/types'
import {MOBILE_SCREENSIZE, TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'
import {analytics} from '@/utils/tracking/analytics'
import {TrademarkDisputeSelectedCase} from '@/utils/trademark-disputes/types'

type ResponseToOppositionProps = {
  relatedQuestions: QuestionsAndAnswersRelatedQuestion[]
  selectedIpCases: TrademarkDisputeSelectedCase[]
  mostRecentArticles: RecentArticleCard[]
}

const ResponseToOpposition: FC<ResponseToOppositionProps> = ({
  relatedQuestions,
  selectedIpCases,
  mostRecentArticles,
}) => {
  const language = useLanguage()
  const {t} = useTranslation()

  const {hero, stats, solutions, howItWorksSteps, pricing} =
    getResponseToOppositionContent(language)
  const {header, requestQuoteButton, showPriceListButton, teamSubtitle, ipCases, pricingBanner} =
    useServiceCommonContent()
  const formRef = useRef<HTMLDivElement>(null)
  const isEnLanguage = language === Locale.en

  return (
    <>
      <Page
        mainCtaButton={{
          text: t(header.buttonLinkText),
          onClick: () => {
            scrollToServiceRequestForm(formRef)
            analytics.track('Click on Request a quote (Response to opposition - Header)')
          },
        }}
      >
        <Hero
          title={t(hero.title)}
          subtitle={t(hero.subtitle)}
          primaryButtonText={t(requestQuoteButton)}
          handleClickOnPrimaryButton={() => {
            scrollToServiceRequestForm(formRef)
            analytics.track('Click on Request a quote (Response to opposition - Hero)')
          }}
          secondaryButtonLink={hero.secondaryButtonLink}
          secondaryButtonText={t(showPriceListButton)}
          secondaryButtonTrackedEvent="Click on Pricing (Response to opposition - Hero)"
        >
          <div className="image">
            <Image
              src={hero.imageSrc}
              alt={hero.imageAlt}
              sizes={`${MOBILE_SCREENSIZE} 160px, ${TABLET_SCREENSIZE} 260px, 365px`}
              fill
              priority
              style={{objectFit: 'contain'}}
            />
          </div>
        </Hero>
        <Section hasPaddingTop>
          <ServiceRequestStatistics
            title={t(stats.title)}
            description={t(stats.description)}
            statistics={stats.list}
          />
        </Section>
        <Section hasPaddingTop>
          <ServiceRequestSolutions
            title={t(solutions.title)}
            description={t(solutions.description)}
            cards={solutions.cards}
          />
        </Section>
        <Section hasPaddingTop>
          <HowServiceRequestWorks steps={howItWorksSteps} isHorizontalLayout hasCheckmark={false} />
        </Section>
        <Section hasPaddingTop>
          <ServiceRequestPricingTable
            description={t(pricing.description)}
            services={pricing.services}
            buttonText={t(requestQuoteButton)}
            onClick={() => scrollToServiceRequestForm(formRef)}
            note={t(pricingBanner.description)}
          />
        </Section>
        <Section hasPaddingTop>
          <LegalTeam subtitle={t(teamSubtitle)} />
        </Section>
        <Testimonial />
        {isEnLanguage && (
          <>
            <Section hasPaddingTop>
              <ServiceRequestFaqs relatedQuestions={relatedQuestions} />
            </Section>
            <Section hasPaddingTop>
              <SelectedIpCases
                title={t(ipCases.title)}
                ipCases={selectedIpCases}
                buttonLinkText={t(ipCases.buttonLinkText)}
                buttonLinkHref={ipCases.buttonLinkHref}
              />
            </Section>
            {mostRecentArticles.length > 0 && (
              <Section hasPaddingTop>
                <MostRecentArticles
                  articles={mostRecentArticles}
                  trackedEvent={{name: 'RecentBlogs-ResponseToOpposition'}}
                />
              </Section>
            )}
          </>
        )}
        <Section hasPaddingTop hasPaddingBottom ref={formRef}>
          <ResponseToOppositionForm />
        </Section>
      </Page>

      <style jsx>{`
        .image {
          position: absolute;
          right: 0;
          width: 365px;
          height: 335px;
          margin-left: ${theme.spacing[8]};
          margin-right: -${theme.spacing[5]};
          margin-bottom: -${HERO_PADDING_DESKTOP};
        }

        @media ${TABLET_SCREENSIZE} {
          .image {
            position: relative;
            width: 260px;
            height: 250px;
            margin-left: 0;
            margin-right: 0;
            margin-top: ${theme.spacing[6]};
          }
        }

        @media ${MOBILE_SCREENSIZE} {
          .image {
            width: 160px;
            height: 150px;
            margin-top: ${theme.spacing[5]};
            margin-bottom: -${HERO_PADDING_MOBILE};
          }
        }
      `}</style>
    </>
  )
}

export default ResponseToOpposition
